import React from "react";

import { MenuItemListDto } from "services/menu/restModels";

import { localizedText } from "shared/lib/localizedMap";
import {
    Button,
    DataTable,
    DataTableColumn,
    EmptyState,
    Pagination,
    SearchInput,
    Select,
    SelectOption,
    Skeleton,
} from "shared/ui";

import { useStopListContext } from "features/StopList/context";
import {
    CategoryField,
    Dot,
    Filters,
    FullLabel,
    Message,
    RowAction,
    SearchField,
    ShortLabel,
} from "features/StopList/styles";

const ALL_CATEGORIES = "all";

export const ItemsTab = () => {
    const state = useStopListContext();
    const { list } = state;

    const categoryOptions: SelectOption[] = [
        { value: ALL_CATEGORIES, label: "Все категории" },
        ...state.categoryOptions.map((category) => ({
            value: category.id,
            label: `${localizedText(category.name, state.locale)} · ${category.stopListItemsCount}`,
        })),
    ];

    const columns: DataTableColumn<MenuItemListDto>[] = [
        { key: "dot", role: "indicator", render: () => <Dot $tone="danger" /> },
        {
            key: "name",
            title: "Позиция",
            role: "primary",
            render: (item) => localizedText(item.name, state.locale),
        },
        {
            key: "category",
            title: "Категория",
            role: "secondary",
            width: 220,
            render: (item) => state.categoryNameById(item.menuCategoryId),
        },
        {
            key: "price",
            title: "Цена",
            role: "secondary",
            width: 100,
            align: "right",
            render: (item) => state.formatPrice(item.sellPrice.price),
        },
        {
            key: "action",
            role: "action",
            width: 176,
            render: (item) => (
                <RowAction>
                    <Button
                        variant="outlined"
                        color="neutral"
                        size="compact"
                        onClick={() => state.returnItemToSale(item)}
                    >
                        <FullLabel>Вернуть в продажу</FullLabel>
                        <ShortLabel>Вернуть</ShortLabel>
                    </Button>
                </RowAction>
            ),
        },
    ];

    const filtered = state.itemsQuery.trim() !== "" || state.categoryFilterId !== null;

    const renderBody = () => {
        if (list.error) return <Message>Не удалось загрузить стоп-лист</Message>;

        if (list.items.length === 0) {
            // Скелетон вместо пустоты: без него на время запроса мелькает «ничего не найдено».
            if (list.loading) return <Skeleton height="360px" />;

            return filtered ? (
                <EmptyState
                    title="Ничего не найдено"
                    description="Попробуйте изменить запрос или выбрать другую категорию."
                    action={
                        <Button variant="text" onClick={state.resetItemsFilters}>
                            Сбросить фильтры
                        </Button>
                    }
                />
            ) : (
                <EmptyState
                    title="Сейчас в стоп-листе ничего нет"
                    description="Все позиции меню доступны гостям."
                />
            );
        }

        return (
            <DataTable
                columns={columns}
                rows={list.items}
                rowKey={(item) => item.id}
                footer={
                    <Pagination
                        page={state.itemsPage}
                        pageSize={state.pageSize}
                        total={list.total}
                        onChange={state.setItemsPage}
                    />
                }
            />
        );
    };

    return (
        <>
            <Filters>
                <SearchField>
                    <SearchInput
                        value={state.itemsQuery}
                        placeholder="Поиск по названию"
                        onChange={state.setItemsQuery}
                    />
                </SearchField>

                {state.categoryOptions.length > 0 && (
                    <CategoryField>
                        <Select
                            value={state.categoryFilterId ?? ALL_CATEGORIES}
                            options={categoryOptions}
                            onChange={(value) =>
                                state.setCategoryFilterId(
                                    value === ALL_CATEGORIES ? null : Number(value)
                                )
                            }
                        />
                    </CategoryField>
                )}
            </Filters>

            {renderBody()}
        </>
    );
};
